<script setup lang="ts">
  import { computed, ref, provide, watch } from 'vue'
  import { X, ChevronLeft } from 'lucide-vue-next'
  import { Dialog, DialogContent } from '@/components/ui/dialog'
  import { getComponent } from '@/components/questionaire/componentTypesV3'
  import {
    FieldTypeEnum,
    type QuestionnaireQuestion,
    type QuestionnaireSection
  } from '@/types/generated'
  import { useTranslation } from '@/composables/useTranslation'
  import FieldHelp from '@/components/questionaire/formElements/fieldHelp.vue'
  import { useQuestionnaireEditorStore } from '@/store/admin/questionnaire/questionnaireEditor'

  const { t } = useTranslation({
    en: {
      questionPreview: 'Question preview',
      previousQuestion: 'Previous question',
      nextQuestion: 'Next question',
      close: 'Close',
      untitledQuestion: 'Untitled Question',
      option1: 'Option 1',
      option2: 'Option 2',
      option3: 'Option 3',
      yes: 'Yes',
      no: 'No',
      enterEmailPlaceholder: 'Enter your email address here...',
      enterPhonePlaceholder: 'Enter your phone number here...',
      enterNumberPlaceholder: 'Enter a number here...',
      enterDecimalPlaceholder: 'Enter a decimal number here...',
      enterUrlPlaceholder: 'Enter a URL here...',
      selectDatePlaceholder: 'Select a date...',
      enterTimePlaceholder: 'Enter a time...',
      enterAnswerPlaceholder: 'Enter your answer here...',
      noQuestionSelected: 'No question selected for preview'
    },
    de: {
      questionPreview: 'Fragenvorschau',
      previousQuestion: 'Vorherige Frage',
      nextQuestion: 'Nächste Frage',
      close: 'Schließen',
      untitledQuestion: 'Unbenannte Frage',
      option1: 'Auswahl 1',
      option2: 'Auswahl 2',
      option3: 'Auswahl 3',
      yes: 'Ja',
      no: 'Nein',
      enterEmailPlaceholder: 'Geben Sie hier Ihre E-Mail-Adresse ein...',
      enterPhonePlaceholder: 'Geben Sie hier Ihre Telefonnummer ein...',
      enterNumberPlaceholder: 'Geben Sie hier eine Zahl ein...',
      enterDecimalPlaceholder: 'Geben Sie hier eine Dezimalzahl ein...',
      enterUrlPlaceholder: 'Geben Sie hier eine URL ein...',
      selectDatePlaceholder: 'Wählen Sie ein Datum aus...',
      enterTimePlaceholder: 'Geben Sie eine Uhrzeit ein...',
      enterAnswerPlaceholder: 'Geben Sie hier Ihre Antwort ein...',
      noQuestionSelected: 'Keine Frage zur Vorschau ausgewählt'
    }
  })

  const questionnaireEditorStore = useQuestionnaireEditorStore()

  const props = defineProps<{
    open: boolean
    question: QuestionnaireQuestion | null
    sectionUuid: string
  }>()

  const emit = defineEmits<{
    'update:open': [value: boolean]
  }>()

  // Mock form state for preview
  const previewValue = ref<any>('')
  const hasValidationError = ref(false)
  const currentQuestionUuid = ref<string>('')

  // Navigation logic
  const questionnaire = computed(() => questionnaireEditorStore.questionnaire)
  const currentSection = computed(() =>
    questionnaire.value?.schema.find((s) => s.uuid === props.sectionUuid)
  )

  // Get all questions across all sections for navigation
  const allQuestions = computed(() => {
    if (!questionnaire.value) return []

    const questions: Array<{
      question: QuestionnaireQuestion
      section: QuestionnaireSection
      index: number
    }> = []
    questionnaire.value.schema.forEach((section) => {
      section.items.forEach((question, index) => {
        questions.push({ question, section, index })
      })
    })
    return questions
  })

  const currentQuestionIndex = computed(() => {
    const uuid = currentQuestionUuid.value || props.question?.uuid
    return allQuestions.value.findIndex((q) => q.question.uuid === uuid)
  })

  const currentQuestionData = computed(() => {
    const index = currentQuestionIndex.value
    if (index >= 0 && index < allQuestions.value.length) {
      return allQuestions.value[index]
    }
    return null
  })

  const canGoPrevious = computed(() => currentQuestionIndex.value > 0)
  const canGoNext = computed(() => currentQuestionIndex.value < allQuestions.value.length - 1)

  const goToPrevious = () => {
    if (canGoPrevious.value) {
      const prevQuestion = allQuestions.value[currentQuestionIndex.value - 1]
      currentQuestionUuid.value = prevQuestion.question.uuid
      previewValue.value = ''
      questionnaireEditorStore.selectQuestion(prevQuestion.question.uuid)
    }
  }

  const goToNext = () => {
    if (canGoNext.value) {
      const nextQuestion = allQuestions.value[currentQuestionIndex.value + 1]
      currentQuestionUuid.value = nextQuestion.question.uuid
      previewValue.value = ''
      questionnaireEditorStore.selectQuestion(nextQuestion.question.uuid)
    }
  }

  // Use current question or fallback to prop
  const displayQuestion = computed(() => {
    return currentQuestionData.value?.question || props.question
  })

  // Convert question to field format for the form component
  const previewField = computed(() => {
    if (!displayQuestion.value) return null

    const field = {
      uuid: displayQuestion.value.uuid,
      label: displayQuestion.value.label || t('untitledQuestion'),
      help: displayQuestion.value.help || '',
      type: displayQuestion.value.type,
      optional: displayQuestion.value.optional,
      choices: displayQuestion.value.choices || [],
      component: getFieldComponent(displayQuestion.value.type),
      placeholder: getFieldPlaceholder(displayQuestion.value.type),
      validation: []
    }

    // For select/multiselect, ensure we have choices
    if (
      (displayQuestion.value.type === FieldTypeEnum.SELECT ||
        displayQuestion.value.type === FieldTypeEnum.MULTISELECT) &&
      (!displayQuestion.value.choices || displayQuestion.value.choices.length === 0)
    ) {
      field.choices = [t('option1'), t('option2'), t('option3')]
    }

    // For yes/no questions, ensure we have yes/no choices
    if (displayQuestion.value.type === FieldTypeEnum.YESNO) {
      field.choices = [t('yes'), t('no')]
    }

    return field
  })

  // Map field types to component names
  function getFieldComponent(fieldType: FieldTypeEnum): string {
    const componentMap: Partial<Record<FieldTypeEnum, string>> = {
      [FieldTypeEnum.TEXTINPUT]: 'textinput',
      [FieldTypeEnum.TEXTAREA]: 'textarea',
      [FieldTypeEnum.EMAILINPUT]: 'emailinput',
      [FieldTypeEnum.PHONEINPUT]: 'phoneinput',
      [FieldTypeEnum.NUMBERINPUT]: 'numberinput',
      [FieldTypeEnum.DECIMALNUMBERINPUT]: 'decimalnumberinput',
      [FieldTypeEnum.URLINPUT]: 'urlinput',
      [FieldTypeEnum.DATEFIELD]: 'datefield',
      [FieldTypeEnum.TIMEFIELD]: 'timefield',
      [FieldTypeEnum.SELECT]: 'select',
      [FieldTypeEnum.MULTISELECT]: 'multiselect',
      [FieldTypeEnum.YESNO]: 'yesno',
      [FieldTypeEnum.FILEFIELD]: 'filefield',
      [FieldTypeEnum.MULTIFILEFIELD]: 'multifilefield'
    }
    return componentMap[fieldType] || 'textinput'
  }

  // Get appropriate placeholder text
  function getFieldPlaceholder(fieldType: FieldTypeEnum): string {
    const placeholderMap: Partial<Record<FieldTypeEnum, string>> = {
      [FieldTypeEnum.EMAILINPUT]: t('enterEmailPlaceholder'),
      [FieldTypeEnum.PHONEINPUT]: t('enterPhonePlaceholder'),
      [FieldTypeEnum.NUMBERINPUT]: t('enterNumberPlaceholder'),
      [FieldTypeEnum.DECIMALNUMBERINPUT]: t('enterDecimalPlaceholder'),
      [FieldTypeEnum.URLINPUT]: t('enterUrlPlaceholder'),
      [FieldTypeEnum.DATEFIELD]: t('selectDatePlaceholder'),
      [FieldTypeEnum.TIMEFIELD]: t('enterTimePlaceholder')
    }
    return placeholderMap[fieldType] || t('enterAnswerPlaceholder')
  }

  // Mock vueform provider for the field components
  provide('vueform', {
    formState: ref({
      valid: true,
      activeFieldId: props.question?.uuid || '',
      isLastField: false,
      isFirstField: true
    }),
    fieldValidationErrors: ref({}),
    validateField: () => true,
    handleFieldValidationError: () => {},
    saveFieldData: () => {},
    onSubmit: () => {},
    back: () => {},
    handleFieldChange: (uuid: string, value: any) => {
      previewValue.value = value
    }
  })

  const handleClose = () => {
    emit('update:open', false)
    // Reset preview value when closing
    previewValue.value = ''
    currentQuestionUuid.value = ''
  }

  const handleValidationError = (hasError: boolean) => {
    hasValidationError.value = hasError
  }

  // Initialize current question when dialog opens
  const initializeQuestion = () => {
    if (props.question && !currentQuestionUuid.value) {
      currentQuestionUuid.value = props.question.uuid
    }
  }

  // Watch for question prop changes
  watch(
    () => props.question,
    (newQuestion) => {
      if (newQuestion) {
        currentQuestionUuid.value = newQuestion.uuid
        previewValue.value = ''
      }
    }
  )

  // Watch for dialog open state
  watch(
    () => props.open,
    (isOpen) => {
      if (isOpen) {
        initializeQuestion()
      } else {
        // Reset when closing
        currentQuestionUuid.value = ''
        previewValue.value = ''
      }
    }
  )
</script>

<template>
  <Dialog
    :open="open"
    @update:open="handleClose"
  >
    <DialogContent
      class="flex max-w-none items-center justify-center border-0 bg-transparent p-0 shadow-none"
    >
      <!-- Figma Design Container -->
      <div
        class="inline-flex w-[600px] flex-col items-start justify-start gap-4 rounded-xl bg-white p-6
          shadow-[0px_12px_24px_0px_rgba(0,0,0,0.12)] outline outline-1 outline-offset-[-1px] outline-gray-300"
      >
        <!-- Header -->
        <div class="inline-flex items-center justify-between self-stretch">
          <div
            class="justify-start text-center font-['Inter'] text-xl font-semibold leading-loose text-gray-800"
          >
            {{ t('questionPreview') }}
          </div>
          <button
            class="relative flex h-6 w-6 items-center justify-center overflow-hidden rounded-md hover:bg-gray-100"
            @click="handleClose"
          >
            <X class="h-4 w-4 text-gray-600" />
          </button>
        </div>

        <!-- Question Content Card -->
        <div
          v-if="previewField"
          class="flex flex-col items-start justify-start gap-4 self-stretch rounded-xl bg-white p-4
            shadow-[0px_2px_4px_0px_rgba(0,0,0,0.04)] outline outline-1 outline-offset-[-1px] outline-gray-200"
        >
          <!-- Previous Question Button -->
          <button
            v-if="canGoPrevious"
            class="-m-1 inline-flex items-center justify-start rounded-md p-1 hover:bg-gray-50"
            @click="goToPrevious"
          >
            <div class="relative flex h-5 w-5 items-center justify-center overflow-hidden">
              <ChevronLeft class="h-3 w-3 text-gray-600" />
            </div>
            <div class="flex items-center justify-center gap-1.5 px-1.5">
              <div
                class="justify-start font-['Inter'] text-sm font-medium leading-tight text-gray-600"
              >
                {{ t('previousQuestion') }}
              </div>
            </div>
          </button>

          <!-- Question Title and Help -->
          <div class="flex flex-col items-start justify-start self-stretch">
            <div class="justify-start">
              <span class="font-['Inter'] text-lg font-semibold leading-7 text-gray-800">
                {{ previewField.label }}
              </span>
              <span
                v-if="!displayQuestion?.optional"
                class="font-['Inter'] text-lg font-semibold leading-7 text-red-500"
              >
                *
              </span>
            </div>
            <div
              v-if="previewField.help"
              class="justify-start self-stretch font-['Inter'] text-base font-normal leading-normal text-gray-600"
            >
              <FieldHelp :help-text="previewField.help" />
            </div>
          </div>

          <!-- Question Input -->
          <div class="flex w-full flex-col items-start justify-start gap-2">
            <Component
              :is="getComponent(previewField.component)"
              v-if="getComponent(previewField.component)"
              v-model="previewValue"
              :field="previewField"
              :is-preview-mode="true"
              @validation-error="handleValidationError"
            />
          </div>

          <!-- Next Question Button -->
          <button
            v-if="canGoNext"
            class="inline-flex h-10 items-center justify-center overflow-hidden rounded-lg bg-purple-600 p-2
              shadow-[0px_2px_4px_0px_rgba(0,0,0,0.04)] transition-colors hover:bg-purple-700"
            @click="goToNext"
          >
            <div class="flex items-center justify-center gap-2 px-2">
              <div
                class="justify-start font-['Inter'] text-base font-medium leading-normal text-white"
              >
                {{ t('nextQuestion') }}
              </div>
            </div>
          </button>

          <!-- Static Next Button when no next question -->
          <div
            v-else
            class="inline-flex h-10 items-center justify-center overflow-hidden rounded-lg bg-gray-300 p-2
              shadow-[0px_2px_4px_0px_rgba(0,0,0,0.04)]"
          >
            <div class="flex items-center justify-center gap-2 px-2">
              <div
                class="justify-start font-['Inter'] text-base font-medium leading-normal text-gray-600"
              >
                {{ t('nextQuestion') }}
              </div>
            </div>
          </div>
        </div>

        <!-- No Question State -->
        <div
          v-else
          class="flex flex-col items-center justify-center self-stretch rounded-xl bg-gray-50 p-8 text-gray-500"
        >
          <p class="text-base font-medium">{{ t('noQuestionSelected') }}</p>
        </div>
      </div>
    </DialogContent>
  </Dialog>
</template>
